import React, { useState, useEffect, useCallback } from 'react';
import {
  Search, Users, FileText, LogOut, ChevronDown, Eye,
  Layers, ExternalLink, Presentation, LayoutDashboard,
  Image, Table, BookOpen,
} from 'lucide-react';
import { withBackend } from '../utils/api';
import { toast } from 'sonner';

const STATUS_COLORS = {
  draft: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
  in_progress: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
  completed: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
  archived: 'bg-gray-100 text-gray-600 dark:bg-gray-800/30 dark:text-gray-400',
};

// Assignable subscription tiers. The list is refreshed from the backend on
// tab load (GET /api/admin/subscriptions returns `tiers`), so this constant
// is only the pre-fetch fallback.
const DEFAULT_TIERS = ['free', 'starter', 'business', 'team', 'max', 'trial', 'prezlab'];

const TIER_BADGE = {
  free:     'bg-gray-100 text-gray-600 dark:bg-gray-800/40 dark:text-gray-400',
  starter:  'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  business: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
  team:     'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
  max:      'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400',
  trial:    'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
  prezlab:  'bg-gradient-to-r from-primary/15 to-accent/15 text-primary',
  // Legacy alias — same limits as Business on the backend.
  pro:      'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
};

const CONTENT_TYPE_META = {
  presentation: { label: 'Presentation', icon: Presentation, color: 'text-green-600' },
  document:     { label: 'Document',     icon: FileText,     color: 'text-blue-600'  },
  infographic:  { label: 'Infographic',  icon: Image,        color: 'text-purple-600'},
  dashboard:    { label: 'Dashboard',    icon: LayoutDashboard, color: 'text-amber-600' },
  sheets:       { label: 'Sheets',       icon: Table,        color: 'text-teal-600'  },
  analysis:     { label: 'Analysis',     icon: BookOpen,     color: 'text-gray-600'  },
};

export default function AdminDashboard({ onLogout }) {
  const [token, setToken]       = useState(() => sessionStorage.getItem('admin_token') || '');
  const [isLoggedIn, setIsLoggedIn] = useState(!!token);

  // Login form
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);

  // Data
  const [users,    setUsers]    = useState([]);
  const [projects, setProjects] = useState([]);
  const [content,  setContent]  = useState([]);   // shared_reports
  const [selectedUser, setSelectedUser] = useState(null);
  const [searchQuery,  setSearchQuery]  = useState('');
  const [loading,  setLoading]  = useState(false);
  const [activeTab, setActiveTab] = useState('content'); // 'projects' | 'content'
  const [selectedProject, setSelectedProject] = useState(null);
  const [openingProject, setOpeningProject] = useState(null); // project id being opened
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);
  const [trialInvites, setTrialInvites] = useState([]);
  const [trialLabel, setTrialLabel] = useState('');
  const [trialBusy, setTrialBusy] = useState(false);
  const [opusAccess, setOpusAccess] = useState({ open_to_all: false, defaults: [], managed: [] });
  const [opusEmail, setOpusEmail] = useState('');
  const [opusBusy, setOpusBusy] = useState(false);
  const [subs, setSubs] = useState([]);            // user profiles with tier
  const [subTiers, setSubTiers] = useState(DEFAULT_TIERS);
  const [subBusyIds, setSubBusyIds] = useState([]); // user ids with in-flight updates
  const [billingTotals, setBillingTotals] = useState(null); // active subs / MRR / month credits
  // r143: Users tab — full auth-user overview + hard delete
  const [usersOverview, setUsersOverview] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null); // user row pending deletion
  const [deleteConfirm, setDeleteConfirm] = useState('');
  const [deleteBusy, setDeleteBusy] = useState(false);

  const adminHeaders = { 'Content-Type': 'application/json', 'X-Admin-Token': token };

  // ── Login ──────────────────────────────────────────────────────
  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginLoading(true);
    try {
      const resp = await fetch(withBackend('/api/admin/login'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password }),
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.error || 'Login failed');
      setToken(data.token);
      sessionStorage.setItem('admin_token', data.token);
      setIsLoggedIn(true);
      toast.success('Logged in');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoginLoading(false);
    }
  };

  const handleAdminLogout = () => {
    setToken(''); setIsLoggedIn(false);
    sessionStorage.removeItem('admin_token');
    setUsers([]); setProjects([]); setContent([]); setSelectedUser(null);
    if (onLogout) onLogout();
  };

  // ── Data fetching ──────────────────────────────────────────────
  const fetchUsers = useCallback(async () => {
    try {
      const resp = await fetch(withBackend('/api/admin/users'), { headers: adminHeaders });
      if (resp.status === 401) { handleAdminLogout(); return; }
      const data = await resp.json();
      setUsers(data.users || []);
    } catch { toast.error('Failed to load users'); }
  }, [token]);

  const fetchProjects = useCallback(async (userId) => {
    setLoading(true);
    try {
      const url = userId
        ? withBackend(`/api/admin/projects?user_id=${userId}`)
        : withBackend('/api/admin/projects');
      const resp = await fetch(url, { headers: adminHeaders });
      if (resp.status === 401) { handleAdminLogout(); return; }
      const data = await resp.json();
      setProjects(data.projects || []);
    } catch { toast.error('Failed to load projects'); }
    finally { setLoading(false); }
  }, [token]);

  const fetchContent = useCallback(async (userId) => {
    setLoading(true);
    try {
      const url = userId
        ? withBackend(`/api/admin/shared-content?user_id=${userId}`)
        : withBackend('/api/admin/shared-content');
      const resp = await fetch(url, { headers: adminHeaders });
      if (resp.status === 401) { handleAdminLogout(); return; }
      const data = await resp.json();
      setContent(data.content || []);
    } catch { toast.error('Failed to load content'); }
    finally { setLoading(false); }
  }, [token]);

  const fetchTrialInvites = useCallback(async () => {
    try {
      const resp = await fetch(withBackend('/api/admin/trial-invites'), { headers: adminHeaders });
      if (resp.status === 401) { handleAdminLogout(); return; }
      const data = await resp.json();
      setTrialInvites(data.invites || []);
    } catch { toast.error('Failed to load trial links'); }
  }, [token]);

  const generateTrialLink = async () => {
    setTrialBusy(true);
    try {
      const resp = await fetch(withBackend('/api/admin/trial-invites'), {
        method: 'POST', headers: adminHeaders,
        body: JSON.stringify({ label: trialLabel.trim() || null, count: 1 }),
      });
      if (resp.status === 401) { handleAdminLogout(); return; }
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.error || 'Failed to generate link');
      const url = data.invites?.[0]?.url;
      if (url) {
        try { await navigator.clipboard.writeText(url); toast.success('Link generated and copied'); }
        catch { toast.success('Link generated'); }
      }
      setTrialLabel('');
      fetchTrialInvites();
    } catch (err) { toast.error(err.message); }
    finally { setTrialBusy(false); }
  };

  const copyLink = async (url) => {
    try { await navigator.clipboard.writeText(url); toast.success('Copied'); }
    catch { toast.error('Copy failed'); }
  };

  const fetchOpusAccess = useCallback(async () => {
    try {
      const resp = await fetch(withBackend('/api/admin/opus-access'), { headers: adminHeaders });
      if (resp.status === 401) { handleAdminLogout(); return; }
      const data = await resp.json();
      setOpusAccess({ open_to_all: !!data.open_to_all, defaults: data.defaults || [], managed: data.managed || [] });
    } catch { toast.error('Failed to load Opus access'); }
  }, [token]);

  const opusPost = async (body) => {
    setOpusBusy(true);
    try {
      const resp = await fetch(withBackend('/api/admin/opus-access'), {
        method: 'POST', headers: adminHeaders, body: JSON.stringify(body),
      });
      if (resp.status === 401) { handleAdminLogout(); return; }
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.error || 'Failed to update');
      setOpusAccess({ open_to_all: !!data.open_to_all, defaults: data.defaults || [], managed: data.managed || [] });
    } catch (err) { toast.error(err.message); }
    finally { setOpusBusy(false); }
  };

  const addOpusEmail = () => {
    const e = opusEmail.trim().toLowerCase();
    if (!e || !e.includes('@')) { toast.error('Enter a valid email'); return; }
    opusPost({ action: 'add', email: e });
    setOpusEmail('');
  };

  const fetchSubscriptions = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await fetch(withBackend('/api/admin/subscriptions'), { headers: adminHeaders });
      if (resp.status === 401) { handleAdminLogout(); return; }
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.error || 'Failed to load plans');
      setSubs(data.users || []);
      if (Array.isArray(data.tiers) && data.tiers.length) setSubTiers(data.tiers);
      setBillingTotals(data.billing || null);
    } catch (err) { toast.error(err.message || 'Failed to load plans'); }
    finally { setLoading(false); }
  }, [token]);

  // Pull one user's subscription state straight from Stripe and re-mirror
  // it — the recovery for a missed webhook, and the certainty button when a
  // row looks wrong.
  const syncFromStripe = async (userId) => {
    setSubBusyIds(ids => [...ids, userId]);
    try {
      const resp = await fetch(withBackend('/api/admin/billing/sync'), {
        method: 'POST', headers: adminHeaders,
        body: JSON.stringify({ user_id: userId }),
      });
      if (resp.status === 401) { handleAdminLogout(); return; }
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.error || 'Sync failed');
      if (data.synced) {
        toast.success(`Synced from Stripe: ${data.sub?.tier || 'free'} (${data.sub?.status})`);
        fetchSubscriptions();
      } else {
        toast.info(data.reason === 'not_stripe_managed'
          ? 'No Stripe customer for this user — their plan is managed manually.'
          : 'No subscriptions found in Stripe for this customer.');
      }
    } catch (err) { toast.error(err.message); }
    finally { setSubBusyIds(ids => ids.filter(id => id !== userId)); }
  };

  // force=true re-sends the current tier (used to restart a trial window —
  // the backend re-stamps trial_ends_at on every trial assignment).
  const assignTier = async (userId, tier, force = false) => {
    const prev = subs.find(u => u.id === userId)?.tier;
    if (prev === tier && !force) return;
    setSubBusyIds(ids => [...ids, userId]);
    try {
      const resp = await fetch(withBackend('/api/admin/subscriptions'), {
        method: 'POST', headers: adminHeaders,
        body: JSON.stringify({ user_id: userId, tier }),
      });
      if (resp.status === 401) { handleAdminLogout(); return; }
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.error || 'Failed to assign plan');
      // Backend clears trial_ends_at on non-trial assignment; mirror it.
      setSubs(cur => cur.map(u => u.id === userId
        ? { ...u, tier: data.tier, trial_ends_at: data.tier === 'trial' ? data.trial_ends_at : null }
        : u));
      toast.success(data.tier === 'trial' ? 'Trial window restarted (7 days)' : `Plan set to ${data.tier}`);
      // A manual assignment on a Stripe-managed account is temporary; the
      // backend says so and the admin must see it NOW, not next month.
      if (data.warning) toast.warning(data.warning, { duration: 12000 });
    } catch (err) { toast.error(err.message); }
    finally { setSubBusyIds(ids => ids.filter(i => i !== userId)); }
  };

  useEffect(() => {
    if (!isLoggedIn || !token) return;
    fetchUsers();
    fetchProjects();
    fetchContent();
  }, [isLoggedIn, token]);

  const handleUserFilter = (userId) => {
    setSelectedUser(userId);
    setSelectedProject(null);
    setUserDropdownOpen(false);
    fetchProjects(userId);
    fetchContent(userId);
  };

  const fetchUsersOverview = useCallback(async () => {
    setUsersLoading(true);
    try {
      const resp = await fetch(withBackend('/api/admin/users/overview'), { headers: adminHeaders });
      if (resp.status === 401) { handleAdminLogout(); return; }
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.error || 'Failed to load users');
      setUsersOverview(data.users || []);
    } catch (err) { toast.error(err.message); }
    finally { setUsersLoading(false); }
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDeleteUser = async () => {
    if (!deleteTarget) return;
    setDeleteBusy(true);
    try {
      const resp = await fetch(withBackend(`/api/admin/users/${deleteTarget.id}`), {
        method: 'DELETE',
        headers: adminHeaders,
        body: JSON.stringify({ confirm_email: deleteConfirm.trim() }),
      });
      const data = await resp.json();
      if (!resp.ok && !data.auth_deleted) throw new Error(data.error || data.auth_error || 'Delete failed');
      const rowCount = Object.values(data.rows_deleted || {}).reduce((a, b) => a + (b || 0), 0);
      toast.success(`Deleted ${data.email} — ${rowCount} rows + auth account removed`);
      const errs = Object.keys(data.row_errors || {});
      if (errs.length) toast.warning(`Some tables could not be purged: ${errs.join(', ')}`, { duration: 12000 });
      setUsersOverview(cur => cur.filter(u => u.id !== deleteTarget.id));
      setDeleteTarget(null);
      setDeleteConfirm('');
    } catch (err) { toast.error(err.message); }
    finally { setDeleteBusy(false); }
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setSearchQuery('');
    if (tab === 'trials') fetchTrialInvites();
    if (tab === 'opus') fetchOpusAccess();
    if (tab === 'plans') fetchSubscriptions();
    if (tab === 'users') fetchUsersOverview();
  };

  const getUserLabel = (userId) => {
    const u = users.find(u => u.id === userId);
    if (!u) return userId?.slice(0, 8) || 'Unknown';
    return u.display_name || u.email || u.id.slice(0, 8);
  };

  const fetchProjectDetail = async (projectId) => {
    try {
      const resp = await fetch(withBackend(`/api/admin/projects/${projectId}`), { headers: adminHeaders });
      const data = await resp.json();
      setSelectedProject(data.project || null);
    } catch { toast.error('Failed to load project details'); }
  };

  const openProject = async (projectId) => {
    setOpeningProject(projectId);
    try {
      const resp = await fetch(withBackend(`/api/admin/projects/${projectId}/open`), {
        method: 'POST',
        headers: adminHeaders,
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.error || 'Failed to open project');
      window.open(data.url, '_blank');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setOpeningProject(null);
    }
  };

  // ── Filtering ──────────────────────────────────────────────────
  const q = searchQuery.toLowerCase();

  const filteredProjects = projects.filter(p =>
    !q || (p.name || '').toLowerCase().includes(q) ||
    (p.client_name || '').toLowerCase().includes(q) ||
    (p.status || '').toLowerCase().includes(q)
  );

  const filteredContent = content.filter(c =>
    !q || (c.presentation_name || '').toLowerCase().includes(q) ||
    (c.content_type || '').toLowerCase().includes(q)
  );

  const filteredUsersOverview = usersOverview.filter(u =>
    !q || (u.email || '').toLowerCase().includes(q) ||
    (u.display_name || '').toLowerCase().includes(q) ||
    (u.tier || '').toLowerCase().includes(q) ||
    (u.id || '').toLowerCase().includes(q)
  );

  const filteredSubs = subs.filter(u =>
    !q || (u.email || '').toLowerCase().includes(q) ||
    (u.full_name || '').toLowerCase().includes(q) ||
    (u.tier || '').toLowerCase().includes(q)
  );

  const totalDocs = activeTab === 'content' ? filteredContent.length
    : activeTab === 'plans' ? filteredSubs.length
    : filteredProjects.length;

  // ── Login Screen ───────────────────────────────────────────────
  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="w-full max-w-sm">
          <div className="text-center mb-8">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-primary to-accent flex items-center justify-center mx-auto mb-4 shadow-lg">
              <Layers className="w-6 h-6 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-foreground">Admin Panel</h1>
            <p className="text-sm text-muted-foreground mt-1">wizardly.ai</p>
          </div>
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">Username</label>
              <input
                type="text"
                value={username}
                onChange={e => setUsername(e.target.value)}
                className="w-full px-3 py-2.5 rounded-xl border border-border bg-card text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-accent/50"
                placeholder="admin"
                autoFocus
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">Password</label>
              <input
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                className="w-full px-3 py-2.5 rounded-xl border border-border bg-card text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-accent/50"
                placeholder="••••••••"
              />
            </div>
            <button
              type="submit"
              disabled={loginLoading || !username || !password}
              className="w-full btn-gradient rounded-xl py-2.5 font-medium text-sm disabled:opacity-50"
            >
              {loginLoading ? 'Signing in...' : 'Sign In'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  // ── Admin Dashboard ────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-card/80 backdrop-blur-sm sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-primary to-accent flex items-center justify-center">
              <Layers className="w-4 h-4 text-white" />
            </div>
            <h1 className="text-lg font-bold text-foreground">Admin Panel</h1>
            <span className="text-xs bg-accent/10 text-accent px-2 py-0.5 rounded-full font-medium">
              {users.length} users · {content.length} items
            </span>
          </div>
          <button
            onClick={handleAdminLogout}
            className="flex items-center gap-2 text-sm text-muted-foreground hover:text-destructive transition-colors"
          >
            <LogOut className="w-4 h-4" />
            Logout
          </button>
        </div>

        {/* Tabs */}
        <div className="max-w-7xl mx-auto px-6 flex gap-1 pb-0">
          {[
            { id: 'users',    label: 'Users',    count: usersOverview.length },
            { id: 'content',  label: 'Content',  count: content.length  },
            { id: 'projects', label: 'Projects', count: projects.length },
            { id: 'plans',    label: 'Plans', count: subs.length },
            { id: 'trials',   label: 'Trial Links', count: trialInvites.length },
            { id: 'opus',     label: 'Opus Access', count: opusAccess.managed.length },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => handleTabChange(tab.id)}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-accent text-accent'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              {tab.label}
              <span className="ml-1.5 text-xs bg-muted px-1.5 py-0.5 rounded-full">{tab.count}</span>
            </button>
          ))}
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-6">
        {/* Filters */}
        <div className="flex items-center gap-4 mb-6">
          {/* User filter */}
          <div className="relative">
            <button
              onClick={() => setUserDropdownOpen(!userDropdownOpen)}
              className="flex items-center gap-2 px-3 py-2 rounded-xl border border-border bg-card text-sm hover:border-accent/50 transition-colors"
            >
              <Users className="w-4 h-4 text-muted-foreground" />
              <span className="text-foreground max-w-[140px] truncate">
                {selectedUser ? getUserLabel(selectedUser) : 'All Users'}
              </span>
              <ChevronDown className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
            </button>
            {userDropdownOpen && (
              <div className="absolute top-full left-0 mt-1 w-72 bg-card border border-border rounded-xl shadow-xl py-1 z-50">
                <button
                  onClick={() => handleUserFilter(null)}
                  className={`w-full text-left px-4 py-2 text-sm hover:bg-muted/50 transition-colors ${!selectedUser ? 'text-accent font-medium' : 'text-foreground'}`}
                >
                  All Users
                </button>
                <div className="border-t border-border/50 my-1" />
                <div className="max-h-64 overflow-y-auto">
                  {users.map(u => (
                    <button
                      key={u.id}
                      onClick={() => handleUserFilter(u.id)}
                      className={`w-full text-left px-4 py-2 text-sm hover:bg-muted/50 transition-colors ${selectedUser === u.id ? 'text-accent font-medium' : 'text-foreground'}`}
                    >
                      <div className="font-medium truncate">{u.display_name || u.email}</div>
                      {u.display_name && u.email !== 'unknown' && (
                        <div className="text-[11px] text-muted-foreground truncate">{u.email}</div>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Search */}
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <input
              type="text"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder={activeTab === 'content' ? 'Search content...' : activeTab === 'plans' ? 'Search users or plans...' : activeTab === 'users' ? 'Search by email, name, tier, or id...' : 'Search projects...'}
              className="w-full pl-9 pr-3 py-2 rounded-xl border border-border bg-card text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-accent/50"
            />
          </div>

          <span className="text-xs text-muted-foreground">{totalDocs} items</span>
        </div>

        {/* ── Content Tab ── */}
        {activeTab === 'users' && (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">
                {usersLoading ? 'Loading users...' : `${filteredUsersOverview.length} of ${usersOverview.length} users · counts are all-time, usage is this month`}
              </p>
              <button onClick={fetchUsersOverview} className="text-xs text-primary underline hover:no-underline">Refresh</button>
            </div>
            <div className="border border-border rounded-xl overflow-x-auto bg-card">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border text-left text-xs text-muted-foreground">
                    <th className="px-4 py-2.5 font-medium">User</th>
                    <th className="px-3 py-2.5 font-medium">Tier</th>
                    <th className="px-3 py-2.5 font-medium">Joined</th>
                    <th className="px-3 py-2.5 font-medium">Last sign-in</th>
                    <th className="px-3 py-2.5 font-medium text-right">Projects</th>
                    <th className="px-3 py-2.5 font-medium text-right">Brands</th>
                    <th className="px-3 py-2.5 font-medium text-right">Gens (mo)</th>
                    <th className="px-3 py-2.5 font-medium text-right">Credits (mo)</th>
                    <th className="px-3 py-2.5 font-medium text-right">Balance</th>
                    <th className="px-3 py-2.5" />
                  </tr>
                </thead>
                <tbody>
                  {filteredUsersOverview.map(u => (
                    <tr key={u.id} className="border-b border-border/50 last:border-0 hover:bg-muted/30">
                      <td className="px-4 py-2.5">
                        <div className="font-medium text-foreground">{u.email || u.id.slice(0, 8)}</div>
                        {u.display_name && <div className="text-xs text-muted-foreground">{u.display_name}</div>}
                      </td>
                      <td className="px-3 py-2.5">
                        <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${TIER_BADGE[u.tier] || TIER_BADGE.free}`}>{u.tier}</span>
                      </td>
                      <td className="px-3 py-2.5 text-xs text-muted-foreground whitespace-nowrap">{u.created_at ? new Date(u.created_at).toLocaleDateString('en-GB') : '—'}</td>
                      <td className="px-3 py-2.5 text-xs text-muted-foreground whitespace-nowrap">{u.last_sign_in_at ? new Date(u.last_sign_in_at).toLocaleDateString('en-GB') : '—'}</td>
                      <td className="px-3 py-2.5 text-right tabular-nums">{u.projects}</td>
                      <td className="px-3 py-2.5 text-right tabular-nums">{u.brands}</td>
                      <td className="px-3 py-2.5 text-right tabular-nums" title={`${u.month_items} slides/pages this month`}>{u.month_generations}</td>
                      <td className="px-3 py-2.5 text-right tabular-nums">{u.month_usage_credits ?? '—'}</td>
                      <td className="px-3 py-2.5 text-right tabular-nums">{u.credits_balance ?? '—'}</td>
                      <td className="px-3 py-2.5 text-right">
                        <button
                          onClick={() => { setDeleteTarget(u); setDeleteConfirm(''); }}
                          className="text-xs text-destructive/80 hover:text-destructive underline hover:no-underline"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                  {!usersLoading && filteredUsersOverview.length === 0 && (
                    <tr><td colSpan={10} className="px-4 py-8 text-center text-sm text-muted-foreground">No users match.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
            {deleteTarget && (
              <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => !deleteBusy && setDeleteTarget(null)}>
                <div className="w-full max-w-md bg-card border border-border rounded-xl p-5 space-y-4" onClick={e => e.stopPropagation()}>
                  <h3 className="text-base font-semibold text-foreground">Delete {deleteTarget.email}?</h3>
                  <p className="text-sm text-muted-foreground">
                    This permanently removes the account from Supabase auth AND every row they own:
                    {' '}{deleteTarget.projects} project{deleteTarget.projects === 1 ? '' : 's'}, {deleteTarget.brands} brand{deleteTarget.brands === 1 ? '' : 's'}, usage history, and credit ledger. It cannot be undone.
                  </p>
                  <div className="space-y-1.5">
                    <label className="text-xs font-medium text-foreground">Type the user's email to confirm</label>
                    <input
                      value={deleteConfirm}
                      onChange={e => setDeleteConfirm(e.target.value)}
                      placeholder={deleteTarget.email}
                      className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-destructive/40"
                    />
                  </div>
                  <div className="flex justify-end gap-2">
                    <button onClick={() => setDeleteTarget(null)} disabled={deleteBusy} className="px-3 py-1.5 text-sm rounded-lg border border-border text-muted-foreground hover:text-foreground disabled:opacity-50">Cancel</button>
                    <button
                      onClick={handleDeleteUser}
                      disabled={deleteBusy || deleteConfirm.trim().toLowerCase() !== (deleteTarget.email || '').toLowerCase()}
                      className="px-3 py-1.5 text-sm rounded-lg bg-destructive text-destructive-foreground disabled:opacity-40"
                    >
                      {deleteBusy ? 'Deleting...' : 'Delete permanently'}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'content' && (
          <div className="bg-card border border-border rounded-xl overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border text-left">
                  <th className="px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Title</th>
                  <th className="px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Type</th>
                  <th className="px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">User</th>
                  <th className="px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Views</th>
                  <th className="px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Slides</th>
                  <th className="px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Created</th>
                  <th className="px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider w-16"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border/50">
                {loading ? (
                  <tr><td colSpan={7} className="px-4 py-12 text-center text-sm text-muted-foreground">Loading...</td></tr>
                ) : filteredContent.length === 0 ? (
                  <tr><td colSpan={7} className="px-4 py-12 text-center text-sm text-muted-foreground">No content found</td></tr>
                ) : filteredContent.map(item => {
                  const meta = CONTENT_TYPE_META[item.content_type] || CONTENT_TYPE_META.document;
                  const Icon = meta.icon;
                  return (
                    <tr key={item.id} className="hover:bg-muted/30 transition-colors">
                      <td className="px-4 py-3">
                        <p className="text-sm font-medium text-foreground truncate max-w-[280px]">
                          {item.presentation_name || 'Untitled'}
                        </p>
                        <p className="text-[11px] text-muted-foreground font-mono">{item.report_hash}</p>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1.5">
                          <Icon className={`w-3.5 h-3.5 ${meta.color}`} />
                          <span className="text-xs text-foreground">{meta.label}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <button
                          onClick={() => handleUserFilter(item.user_id)}
                          className="text-xs text-accent hover:underline truncate max-w-[150px] block"
                        >
                          {getUserLabel(item.user_id)}
                        </button>
                      </td>
                      <td className="px-4 py-3 text-xs text-muted-foreground">{item.view_count ?? 0}</td>
                      <td className="px-4 py-3 text-xs text-muted-foreground">{item.slide_count ?? '-'}</td>
                      <td className="px-4 py-3 text-xs text-muted-foreground">
                        {item.created_at ? new Date(item.created_at).toLocaleDateString() : '-'}
                      </td>
                      <td className="px-4 py-3">
                        <button
                          onClick={() => window.open(`/share/${item.report_hash}`, '_blank')}
                          className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-accent/10 text-accent hover:bg-accent/20 transition-colors text-xs font-medium"
                          title="Open document"
                        >
                          <ExternalLink className="w-3 h-3" />
                          View
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* ── Plans Tab ── */}
        {activeTab === 'plans' && (
          <div className="space-y-3">
            <p className="text-[11px] text-muted-foreground">
              Assigning a plan takes effect on the user's next generation. Paid plans (Starter, Business, Team, Max)
              unlock premium models within their monthly caps even while Opus access is paused. Assigning Trial starts a 7-day window
              (use Restart on an existing trial to extend it). Note: @prezlab.com accounts assigned Free auto-upgrade back to
              Prezlab on their next login — assign Starter to simulate a capped plan for internal testers.
              A ⚡ badge means the plan is Stripe-managed: manual assignments there revert on the next Stripe event.
            </p>
            {billingTotals && (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {[
                  { label: 'Active subscriptions', value: billingTotals.active_subscriptions ?? 0 },
                  { label: 'MRR', value: `$${((billingTotals.mrr_cents || 0) / 100).toLocaleString('en-US')}` },
                  { label: 'Usage this month', value: `${(billingTotals.month_usage_credits || 0).toLocaleString('en-US')} cr` },
                  { label: 'Charged this month', value: `${(billingTotals.month_charged_credits || 0).toLocaleString('en-US')} cr` },
                ].map(c => (
                  <div key={c.label} className="bg-card border border-border rounded-xl px-4 py-3">
                    <p className="text-[10px] uppercase tracking-wider text-muted-foreground">{c.label}</p>
                    <p className="text-lg font-semibold text-foreground tabular-nums">{c.value}</p>
                  </div>
                ))}
              </div>
            )}
            <div className="bg-card border border-border rounded-xl overflow-hidden overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border text-left">
                    <th className="px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">User</th>
                    <th className="px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Plan</th>
                    <th className="px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Subscription</th>
                    <th className="px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider text-right">Credits</th>
                    <th className="px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider text-right">Used / mo</th>
                    <th className="px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Assign</th>
                    <th className="px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Trial ends</th>
                    <th className="px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Joined</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border/50">
                  {loading ? (
                    <tr><td colSpan={8} className="px-4 py-12 text-center text-sm text-muted-foreground">Loading...</td></tr>
                  ) : filteredSubs.length === 0 ? (
                    <tr><td colSpan={8} className="px-4 py-12 text-center text-sm text-muted-foreground">No users found</td></tr>
                  ) : filteredSubs.map(u => (
                    <tr key={u.id} className="hover:bg-muted/30 transition-colors">
                      <td className="px-4 py-3">
                        <p className="text-sm font-medium text-foreground truncate max-w-[240px]">{u.full_name || u.email || u.id.slice(0, 8)}</p>
                        {u.full_name && u.email && (
                          <p className="text-[11px] text-muted-foreground truncate max-w-[240px]">{u.email}</p>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`text-[11px] px-2 py-0.5 rounded-full font-medium capitalize ${TIER_BADGE[u.tier] || TIER_BADGE.free}`}>
                          {u.tier || 'free'}
                        </span>
                        {u.tier_drift && (
                          <span
                            className="ml-1.5 text-[10px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 font-medium"
                            title={`Stripe says '${u.subscription_tier}' — this manual tier reverts on the next Stripe event`}
                          >
                            ≠ Stripe
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {u.stripe_customer_id ? (
                          <div className="flex items-center gap-1.5">
                            <span className="text-[11px] text-foreground capitalize" title="Stripe-managed">
                              ⚡ {u.subscription_tier || '—'}
                              <span className="text-muted-foreground"> · {u.subscription_status}</span>
                            </span>
                            <button
                              onClick={() => syncFromStripe(u.id)}
                              disabled={subBusyIds.includes(u.id)}
                              className="text-[10px] px-1.5 py-0.5 rounded bg-muted hover:bg-muted/70 text-muted-foreground transition-colors disabled:opacity-50"
                              title="Re-pull this user's subscription state from Stripe"
                            >
                              sync
                            </button>
                          </div>
                        ) : (
                          <span className="text-[11px] text-muted-foreground">manual</span>
                        )}
                        {u.current_period_end && (
                          <p className="text-[10px] text-muted-foreground mt-0.5">
                            {u.cancel_at_period_end ? 'ends ' : 'renews '}
                            {new Date(u.current_period_end).toLocaleDateString()}
                          </p>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right text-xs tabular-nums text-foreground">
                        {typeof u.credits_balance === 'number' ? u.credits_balance.toLocaleString('en-US') : '—'}
                      </td>
                      <td className="px-4 py-3 text-right text-xs tabular-nums text-muted-foreground">
                        {typeof u.month_usage_credits === 'number' ? u.month_usage_credits.toLocaleString('en-US') : '—'}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <select
                            value={u.tier || 'free'}
                            onChange={e => assignTier(u.id, e.target.value)}
                            disabled={subBusyIds.includes(u.id)}
                            className="px-2 py-1.5 rounded-lg border border-border bg-card text-xs text-foreground capitalize focus:outline-none focus:ring-2 focus:ring-accent/50 disabled:opacity-50"
                          >
                            {/* Legacy/unknown tiers (e.g. 'pro') stay visible instead of a blank select */}
                            {!subTiers.includes(u.tier || 'free') && (
                              <option value={u.tier} disabled className="capitalize">{u.tier} (legacy)</option>
                            )}
                            {subTiers.map(t => (
                              <option key={t} value={t} className="capitalize">{t}</option>
                            ))}
                          </select>
                          {u.tier === 'trial' && (
                            <button
                              onClick={() => assignTier(u.id, 'trial', true)}
                              disabled={subBusyIds.includes(u.id)}
                              className="px-2 py-1.5 rounded-lg bg-accent/10 text-accent hover:bg-accent/20 transition-colors text-xs font-medium disabled:opacity-50"
                              title="Restart the 7-day trial window"
                            >
                              Restart
                            </button>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-xs text-muted-foreground">
                        {u.tier === 'trial' && u.trial_ends_at
                          ? new Date(u.trial_ends_at).toLocaleDateString()
                          : '—'}
                      </td>
                      <td className="px-4 py-3 text-xs text-muted-foreground">
                        {u.created_at ? new Date(u.created_at).toLocaleDateString() : '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ── Trial Links Tab ── */}
        {activeTab === 'trials' && (
          <div className="space-y-4">
            <div className="bg-card border border-border rounded-xl p-4">
              <div className="flex items-end gap-3 flex-wrap">
                <div className="flex-1 min-w-[200px]">
                  <label className="block text-xs font-medium text-muted-foreground mb-1.5">Lead name / label (optional)</label>
                  <input
                    type="text"
                    value={trialLabel}
                    onChange={e => setTrialLabel(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter' && !trialBusy) generateTrialLink(); }}
                    placeholder="e.g. Mohammad"
                    className="w-full px-3 py-2 rounded-xl border border-border bg-card text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-accent/50"
                  />
                </div>
                <button
                  onClick={generateTrialLink}
                  disabled={trialBusy}
                  className="btn-gradient rounded-xl px-4 py-2 text-sm font-medium disabled:opacity-50"
                >
                  {trialBusy ? 'Generating…' : 'Generate trial link'}
                </button>
              </div>
              <p className="mt-2 text-[11px] text-muted-foreground">
                Each link is single-use and grants a 7-day full trial (Opus decks capped at 16 slides). It is copied to your clipboard automatically.
              </p>
            </div>

            <div className="bg-card border border-border rounded-xl overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border text-left">
                    <th className="px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Label</th>
                    <th className="px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Link</th>
                    <th className="px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Status</th>
                    <th className="px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Created</th>
                    <th className="px-4 py-3 w-16"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border/50">
                  {trialInvites.length === 0 ? (
                    <tr><td colSpan={5} className="px-4 py-12 text-center text-sm text-muted-foreground">No trial links yet</td></tr>
                  ) : trialInvites.map(inv => (
                    <tr key={inv.code} className="hover:bg-muted/30 transition-colors">
                      <td className="px-4 py-3 text-sm text-foreground">{inv.label || '—'}</td>
                      <td className="px-4 py-3">
                        <span className="text-[11px] text-muted-foreground font-mono truncate max-w-[260px] block">{inv.url}</span>
                      </td>
                      <td className="px-4 py-3">
                        {inv.redeemed ? (
                          <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">Redeemed</span>
                        ) : (
                          <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">Pending</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-xs text-muted-foreground">
                        {inv.created_at ? new Date(inv.created_at).toLocaleDateString() : '-'}
                      </td>
                      <td className="px-4 py-3">
                        <button
                          onClick={() => copyLink(inv.url)}
                          className="px-2.5 py-1.5 rounded-lg bg-accent/10 text-accent hover:bg-accent/20 transition-colors text-xs font-medium"
                        >
                          Copy
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ── Opus Access Tab ── */}
        {activeTab === 'opus' && (
          <div className="space-y-4 max-w-2xl">
            <div className="bg-card border border-border rounded-xl p-4">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <p className="text-sm font-medium text-foreground">Open Opus to everyone</p>
                  <p className="text-xs text-muted-foreground mt-0.5">When on, all accounts can use Opus and the allowlist below is ignored.</p>
                </div>
                <button
                  onClick={() => opusPost({ action: 'set_mode', open_to_all: !opusAccess.open_to_all })}
                  disabled={opusBusy}
                  className={`relative w-11 h-6 rounded-full transition-colors flex-shrink-0 ${opusAccess.open_to_all ? 'bg-accent' : 'bg-muted'} disabled:opacity-50`}
                  aria-label="Toggle Opus open to everyone"
                >
                  <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${opusAccess.open_to_all ? 'translate-x-5' : ''}`} />
                </button>
              </div>
            </div>

            <div className="bg-card border border-border rounded-xl p-4">
              <p className="text-sm font-medium text-foreground mb-1">Allowed emails</p>
              <p className="text-xs text-muted-foreground mb-3">
                {opusAccess.open_to_all
                  ? 'Opus is open to everyone — this list is currently ignored.'
                  : 'These accounts can use Opus. Defaults are always allowed and cannot be removed.'}
              </p>
              <div className="flex items-center gap-2 mb-3">
                <input
                  type="email"
                  value={opusEmail}
                  onChange={e => setOpusEmail(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter' && !opusBusy) addOpusEmail(); }}
                  placeholder="name@company.com"
                  className="flex-1 px-3 py-2 rounded-xl border border-border bg-card text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-accent/50"
                />
                <button onClick={addOpusEmail} disabled={opusBusy} className="btn-gradient rounded-xl px-4 py-2 text-sm font-medium disabled:opacity-50">Add</button>
              </div>
              <div className="space-y-1.5">
                {opusAccess.defaults.map(email => (
                  <div key={email} className="flex items-center justify-between px-3 py-2 rounded-lg bg-muted/40">
                    <span className="text-sm text-foreground">{email}</span>
                    <span className="text-[11px] text-muted-foreground">default</span>
                  </div>
                ))}
                {opusAccess.managed.map(email => (
                  <div key={email} className="flex items-center justify-between px-3 py-2 rounded-lg bg-muted/40">
                    <span className="text-sm text-foreground">{email}</span>
                    <button onClick={() => opusPost({ action: 'remove', email })} disabled={opusBusy} className="text-xs text-muted-foreground hover:text-destructive">Remove</button>
                  </div>
                ))}
                {opusAccess.managed.length === 0 && opusAccess.defaults.length === 0 && (
                  <p className="text-xs text-muted-foreground">No emails yet.</p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ── Projects Tab ── */}
        {activeTab === 'projects' && (
          <div className="bg-card border border-border rounded-xl overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border text-left">
                  <th className="px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Document</th>
                  <th className="px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">User</th>
                  <th className="px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Status</th>
                  <th className="px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Slides</th>
                  <th className="px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Score</th>
                  <th className="px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Created</th>
                  <th className="px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider w-12"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border/50">
                {loading ? (
                  <tr><td colSpan={7} className="px-4 py-12 text-center text-sm text-muted-foreground">Loading...</td></tr>
                ) : filteredProjects.length === 0 ? (
                  <tr><td colSpan={7} className="px-4 py-12 text-center text-sm text-muted-foreground">No projects found</td></tr>
                ) : filteredProjects.map(p => (
                  <tr key={p.id} className="hover:bg-muted/30 transition-colors">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        {p.thumbnail_url ? (
                          <img src={p.thumbnail_url} alt="" className="w-10 h-7 rounded object-cover border border-border/50" />
                        ) : (
                          <div className="w-10 h-7 rounded bg-muted flex items-center justify-center">
                            <FileText className="w-3.5 h-3.5 text-muted-foreground" />
                          </div>
                        )}
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-foreground truncate max-w-[240px]">{p.name || 'Untitled'}</p>
                          {p.client_name && <p className="text-[11px] text-muted-foreground">{p.client_name}</p>}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <button onClick={() => handleUserFilter(p.user_id)} className="text-xs text-accent hover:underline truncate max-w-[150px] block">
                        {getUserLabel(p.user_id)}
                      </button>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-[11px] px-2 py-0.5 rounded-full font-medium ${STATUS_COLORS[p.status] || STATUS_COLORS.draft}`}>
                        {(p.status || 'draft').replace('_', ' ')}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs text-muted-foreground">{p.slide_count || '-'}</td>
                    <td className="px-4 py-3">
                      {p.overall_score != null ? (
                        <span className={`text-xs font-medium ${p.overall_score >= 80 ? 'text-green-600 dark:text-green-400' : p.overall_score >= 60 ? 'text-amber-600 dark:text-amber-400' : 'text-red-600 dark:text-red-400'}`}>
                          {p.overall_score}
                        </span>
                      ) : <span className="text-xs text-muted-foreground">-</span>}
                    </td>
                    <td className="px-4 py-3 text-xs text-muted-foreground">
                      {p.created_at ? new Date(p.created_at).toLocaleDateString() : '-'}
                    </td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => openProject(p.id)}
                        disabled={openingProject === p.id}
                        className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-accent/10 text-accent hover:bg-accent/20 transition-colors text-xs font-medium disabled:opacity-50"
                        title="Open document"
                      >
                        {openingProject === p.id
                          ? <span className="w-3 h-3 border border-accent border-t-transparent rounded-full animate-spin" />
                          : <ExternalLink className="w-3 h-3" />}
                        View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Project detail modal */}
      {selectedProject && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setSelectedProject(null)}>
          <div className="bg-card border border-border rounded-2xl shadow-2xl w-full max-w-2xl max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="px-6 py-4 border-b border-border flex items-center justify-between">
              <h2 className="text-lg font-semibold text-foreground">{selectedProject.name || 'Untitled'}</h2>
              <button onClick={() => setSelectedProject(null)} className="text-muted-foreground hover:text-foreground text-sm">Close</button>
            </div>
            <div className="px-6 py-4 space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div><span className="text-muted-foreground">User</span><p className="text-foreground mt-0.5">{getUserLabel(selectedProject.user_id)}</p></div>
                <div><span className="text-muted-foreground">Status</span><p className="text-foreground mt-0.5 capitalize">{(selectedProject.status || 'draft').replace('_', ' ')}</p></div>
                <div><span className="text-muted-foreground">Slides</span><p className="text-foreground mt-0.5">{selectedProject.slide_count || 0}</p></div>
                <div><span className="text-muted-foreground">Score</span><p className="text-foreground mt-0.5">{selectedProject.overall_score ?? 'N/A'}</p></div>
                <div><span className="text-muted-foreground">Client</span><p className="text-foreground mt-0.5">{selectedProject.client_name || 'N/A'}</p></div>
                <div><span className="text-muted-foreground">Created</span><p className="text-foreground mt-0.5">{selectedProject.created_at ? new Date(selectedProject.created_at).toLocaleString() : 'N/A'}</p></div>
              </div>
              {selectedProject.description && (
                <div><span className="text-sm text-muted-foreground">Description</span><p className="text-sm text-foreground mt-1">{selectedProject.description}</p></div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Close user dropdown on outside click */}
      {userDropdownOpen && (
        <div className="fixed inset-0 z-30" onClick={() => setUserDropdownOpen(false)} />
      )}
    </div>
  );
}
